import Layout from '@scripts/routes/Layout'
import { gsap } from 'gsap'
import App from '@scripts/app'
import { SplitText } from 'gsap/SplitText'

/**
 * Creates a new *Page*.
 *
 * *Page* is the default *Renderer*.
 *
 * Every other renderer should extend *Page* and implements its useful methods.
 *
 * @class
 * @augments Renderer
 */
export default class GallerySingle extends Layout {

  onEnter() {
    super.onEnter()

    this.isVignetteClick = false
    this.diffX = 15
    this.diffY = 10
    this.isZoom = false
    this.isInit = false

    this.init()
    this.quickTo()
  }

  getElems() {
    super.getElems()

    this.pageHeader = this.view.querySelector('.js-page-header')
    this.titleMain = this.view.querySelector('.js-page-title')
    this.titleMain.big = this.titleMain.querySelector('.t--big')
    this.titleMain.min = this.titleMain.querySelector('.t--min')
    this.titleFill = this.view.querySelector('.js-page-title-fill')
    this.grid = this.view.querySelector('.js-page-grid')
    this.pagination = this.view.querySelector('.js-page-pagination')
    this.paginationTitle = this.view.querySelector('.js-page-pagination-title')
    this.zoom = this.view.querySelector('.js-page-zoom')
    this.zoomImg = this.view.querySelector('.js-page-zoom-img')
    this.zoomBg = this.view.querySelector('.js-page-zoom-bg')
    this.zoomInfo = this.view.querySelector('.js-page-zoom-info')
    this.zoomGallery = this.view.querySelector('.js-page-zoom-gallery')
    this.zoomGalleryInner = this.view.querySelector('.js-page-zoom-gallery-inner')
    this.pictures = this.view.querySelectorAll('.js-cursor-hover')
    this.zoomVignette = false
  }

  onEnterCompleted() {
    super.onEnterCompleted()

    this.scrolltriger()
    App.cursor.toggleScroll(true)
  }

  onFirstEnter() {
    super.onFirstEnter()

    this.prepareInit()
  }

  prepareInit() {
    this.split()
    gsap.set(this.titleMain.split.chars, { x: -80, filter: 'blur(18px)', opacity: 0, scale: 0.95 })
    gsap.set([this.titleFill, this.titleMain.min, this.grid], { opacity: 0 })
  }

  onFirstEnterCompleted() {
    super.onFirstEnterCompleted()

    this.onEnterCompleted()
  }

  onLeave() {
    super.onLeave()

    App.cursor.toggleScroll(false)
  }

  events() {
    super.events()

    this.hVignette = []

    this.pictures.forEach((item, index) => {
      item.nbr = index
      item.img = item.querySelector('img')
      item.source = item.querySelector('source[type="image/webp"]')
      item.imgLoad = item.parentNode.querySelector('.img-loading')
      item.portrait = item.img.naturalHeight > item.img.naturalWidth
      item.info = item.parentNode.dataset.date + ' - ' + item.parentNode.dataset.location
      item.bigImg = item.source.srcset.split(' ')[0]
      if (index < 12) item.pos = item.getBoundingClientRect()

      const div = document.createElement('div')
      const inner = document.createElement('div')

      div.picture = item
      item.vignette = div
      item.vignette.img = inner
      this.hVignette.push(item.vignette)

      gsap.set(item.vignette.img, { backgroundImage: 'url(' + item.imgLoad.currentSrc + ')' })

      item.vignette.appendChild(item.vignette.img)
      this.zoomGalleryInner.appendChild(div)

      this.addEvent({
        target: item, name: 'click', callback: this.zoomToggle
      })

      this.addEvent({
        target: item.vignette, name: 'click', callback: this.vignetteClick.bind(this, item)
      })

      this.addEvent({
        target: item.vignette.img, name: 'mouseenter', callback: this.vignetteHover.bind(this, true)
      })

      this.addEvent({
        target: item.vignette.img, name: 'mouseleave', callback: this.vignetteHover.bind(this, false)
      })

      if (item.img.complete) {
        this.imgLoadHide(item)
      } else {
        this.addEvent({
          target: item.img, name: 'load', callback: this.vignetteLoad
        })
      }
    })

    this.addEvent({
      target: this.zoomBg, name: 'click', callback: this.zoomToggle
    })

    this.addEvent({
      target: document, name: 'keyup', callback: this.keyPress
    })

    this.addEvent({
      target: this.zoom, name: 'mousewheel', callback: this.zoomWheel
    })
  }

  loadBigImg() {
    this.pictures.forEach((item, index) => {
      const img = new Image()

      img.src = item.source.srcset.split(' ')[0]
      item.bigImg = img.src

      this.addEvent({
        target: img, name: 'load', callback: this.bigImgLoad.bind(this, item)
      })
    })
  }

  vignetteLoad(e) {
    const picture = e.currentTarget.parentNode

    gsap.set(picture.vignette.img, { backgroundImage: 'url(' + picture.img.currentSrc + ')' })
    this.imgLoadHide(picture)
  }

  bigImgLoad(item) {
    item.imgFull = item.bigImg
  }

  imgLoadHide(elem) {
    gsap.set(elem.vignette.img, { backgroundImage: 'url(' + elem.img.currentSrc + ')' })
    elem.classList.add('!opacity-100')
    // gsap.to(elem, {
    //   duration: 0.7, opacity: 0, ease: 'quad.out', onComplete: () => {
    //     elem.remove()
    //   }
    // })
  }

  init() {
    gsap.set(this.hVignette, { xPercent: 200, opacity: 0 })
    gsap.set(this.zoomImg, { clipPath: 'inset(50%)' })
  }

  split() {
    this.titleMain.split = new SplitText(this.titleMain.big, { type: 'chars' })
  }

  letter() {
    this.titleMain.big.classList.add('t--letter')
  }

  quickTo() {
    const duration = 2.5
    const ease = 'expo.out'

    this.moveX = gsap.quickTo(this.titleMain.big, 'x', { duration, ease })
    this.moveY = gsap.quickTo(this.titleMain.big, 'y', { duration, ease })
  }

  appear({ from, to, trigger, canvas } = {}) {
    const tl = gsap.timeline({
      onComplete: () => {
        this.letter()
        this.isInit = true
      }
    })

    tl.to(this.titleMain.split.chars, {
      duration: 1.35, x: 0, filter: 'blur(0px)', opacity: 1, scale: 1, stagger: {
        from: 'end', amount: 0.35
      }, ease: 'power2.out'
    }, 'start')

    tl.addLabel('step1', '-=0.7')

    tl.to([this.grid, this.titleMain.min], { duration: 0.7, opacity: 1, ease: 'quad.out' }, 'step1')

    tl.set(this.titleFill, { opacity: 1 })
    tl.set(this.titleMain.split.chars, { clearProps: 'filter' })

    return tl
  }

  scrolltriger() {
    this.tlS = gsap.timeline({
      scrollTrigger: {
        trigger: this.pageHeader, start: 'top top', end: 'bottom 75%'
      }
    })

    this.tlS.to(this.titleMain, { opacity: 0, filter: 'blur(18px)', ease: 'none' })

    if (this.pagination) {
      this.tlP = gsap.timeline({
        scrollTrigger: {
          trigger: this.pagination, start: 'top 25%', end: 'bottom bottom'
        }
      })

      this.tlP.to(this.titleFill, { opacity: 0, ease: 'none' }, 'start')
      this.tlP.to(this.paginationTitle, { opacity: 0.65, ease: 'none' }, 'start')
      this.tlP.to(this.pagination, { opacity: 1, ease: 'none' }, 'start')
    }
  }

  pointermove(e) {
    super.pointermove(e)

    if (!App.isDevice('laptop-min') || !this.isInit) return

    const xPercent = (e.clientX / App.windowWidth) - 0.5
    const yPercent = (e.clientY / App.windowHeight) - 0.5
    const x = xPercent * -this.diffX
    const y = yPercent * -this.diffY

    this.moveX(x)
    this.moveY(y)
  }

  keyPress(e) {
    if (this.isZoom) {
      if (e.which == 27) this.zoomToggle()

      if (!this.isVignetteClick) {
        if (e.which == 39 || e.which == 40) this.vignetteNext(1, e)
        if (e.which == 37 || e.which == 38) this.vignetteNext(-1, e)
      }
    }
  }

  zoomWheel(e) {
    if (!App.isDevice('laptop-min') || this.isVignetteClick) return

    this.vignetteNext(e.deltaY > 0 ? 1 : -1, e)
  }

  zoomToggle(e) {
    if (!App.isDevice('laptop-min')) return

    this.isZoom = this.zoom.classList.toggle('active')

    if (this.tlToggle) this.tlToggle.kill()

    if (this.isZoom) {
      App.cursor.toggleScroll(false)
      App.cursor.toggleClose(true)
      this.zoomGalleryUpdate(e.currentTarget.nbr, 0)
      this.zoomVignette = e.currentTarget.vignette
      this.zoomVignetteToggle(true)
      this.zoomPortrait(e.currentTarget.portrait)
      this.zoomImgFill(e.currentTarget.bigImg)
      this.zoomInfoFill(e.currentTarget.info)
    } else {
      this.vignetteClickReset()
      App.cursor.toggleClose(false)
      App.cursor.toggleScroll(true)
    }

    App.scroll(!this.isZoom)
    App.header.show(!this.isZoom)

    this.tlToggle = gsap.timeline({
      onComplete: () => {
        if (!this.isZoom) {
          this.zoomPortrait(false)
          this.zoomVignetteToggle(false)
        }
      }
    })

    if (this.isZoom) this.tlToggle.add(this.zoomShow(true), 'start')

    this.tlToggle.addLabel('step1', this.isZoom ? '-=0.2' : '')

    this.tlToggle.to(this.hVignette, {
      duration: 0.7, xPercent: this.isZoom ? 0 : 200, opacity: this.isZoom ? 1 : 0, stagger: {
        from: 'center', amount: 0.25
      }, ease: 'power2.out'
    }, this.isZoom ? 'step1' : 'start')

    this.tlToggle.to(this.zoomImg, {
      duration: 0.7, clipPath: this.isZoom ? 'inset(0%)' : 'inset(50%)', ease: 'power2.out'
    }, this.isZoom ? 'step1' : 'start')

    if (!this.isZoom) this.tlToggle.add(this.zoomShow(false), '-=0.4')
  }

  zoomShow(bool) {
    const tl = gsap.timeline()

    tl.to(this.zoom, {
      duration: 0.7, opacity: bool ? 1 : 0, ease: 'power2.out'
    })

    return tl
  }

  zoomPortrait(bool) {
    this.zoom.classList.toggle('portrait', bool)
  }

  zoomVignetteToggle(bool) {
    this.zoomVignette.classList.toggle('active', bool)
  }

  zoomGalleryUpdate(nbr, duration = 1) {
    const diff = this.zoomGalleryInner.offsetHeight - this.zoomGallery.offsetHeight

    if (diff > 0) {
      if (this.zoomGalleryInner.tl) this.zoomGalleryInner.tl.kill()

      this.zoomGalleryInner.tl = gsap.to(this.zoomGalleryInner, {
        duration, y: -((diff / (this.pictures.length - 1))) * nbr, ease: 'power2.out'
      })
    }
  }

  vignetteClick(item, e) {
    e.stopPropagation()
    e.preventDefault()

    const oImg = new Image()

    oImg.src = item.bigImg

    oImg.onload = (() => {
      this.zoomVignetteToggle(false)
      this.zoomVignette = item.vignette
      this.zoomVignetteToggle(true)
      this.zoomGalleryUpdate(item.nbr, 0.7)

      this.zoomPortrait(item.portrait)
      this.zoomImgFill(item.bigImg)
      this.zoomInfoFill(item.info)

      this.isVignetteClick = false
    })

    if (this.isVignetteClick) return

    this.isVignetteClick = true

    // this.tlVignetteUpdate = gsap.timeline({
    //   onComplete: () => this.isVignetteClick = false
    // })
    //
    // this.tlVignetteUpdate.to(this.zoomImg, { duration: 0.7, clipPath: 'inset(50%)', ease: 'power2.out' })
    //
    // this.tlVignetteUpdate.add(() => {
    //   this.zoomImgFill(item.img)
    //   this.zoomInfoFill(item.info)
    //   this.zoomPortrait(item.portrait)
    // })
    //
    // this.tlVignetteUpdate.to(this.zoomImg, { duration: 0.7, clipPath: 'inset(0%)', ease: 'power2.out' })

    return false
  }

  vignetteClickReset() {
    if (this.tlVignetteUpdate) this.tlVignetteUpdate.kill()
    this.isVignetteClick = false
  }

  zoomImgFill(img) {
    gsap.set(this.zoomImg, { backgroundImage: 'url(' + img + ')' })
  }

  zoomInfoFill(value) {
    this.zoomInfo.innerText = value
  }

  vignetteHover(bool) {
    App.cursor.toggleClose(!bool)
    App.cursor.toggleHover(bool)
  }

  vignetteNext(bool, e) {
    let vignetteNew = null

    if (bool < 0) {
      if (!this.zoomVignette.previousElementSibling) return
      vignetteNew = this.zoomVignette.previousElementSibling.picture
    } else {
      if (!this.zoomVignette.nextElementSibling) return
      vignetteNew = this.zoomVignette.nextElementSibling.picture
    }

    this.vignetteClick(vignetteNew, e)
  }
}
